import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * <ErrorBoundary /> — graceful, on-brand fallback for any uncaught render error
 * inside the dashboard. Prevents a single API/component hiccup from crashing
 * the entire authenticated session.
 *
 * Usage:
 *   <ErrorBoundary label="Command Center"><CommandCenter /></ErrorBoundary>
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Structured log for observability — keeps the console useful in production.
    console.error("[ErrorBoundary]", this.props.label || "unknown", error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    const label = this.props.label || "this view";
    return (
      <div
        role="alert"
        data-testid={`error-boundary-${(this.props.label || "view").toLowerCase().replace(/\s+/g, "-")}`}
        className="rounded-2xl border border-rose-400/30 bg-rose-500/5 p-6 sm:p-8 max-w-2xl mx-auto text-center"
      >
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/15 border border-rose-400/40 mb-4">
          <AlertTriangle size={22} className="text-rose-300" />
        </div>
        <h3 className="font-display font-black text-xl text-white mb-2">Dox tripped on something.</h3>
        <p className="text-white/60 text-sm mb-5 max-w-md mx-auto">
          I couldn&apos;t render {label} just now. Try refreshing — I&apos;ll pick up right where we left off. If it
          keeps happening, ping support and I&apos;ll get on it.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            onClick={this.reset}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/15 px-5 py-2.5 text-sm font-semibold text-white min-h-[44px]"
            data-testid="error-boundary-retry"
          >
            <RefreshCw size={14} /> Try again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full bg-dox-orange hover:bg-dox-orangeHover px-5 py-2.5 text-sm font-bold text-white min-h-[44px]"
            data-testid="error-boundary-reload"
          >
            Refresh page
          </button>
        </div>
      </div>
    );
  }
}
